import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../authentication';
import { useNotifications } from '../../../providers/NotificationsProvider';
import { useOrganizations } from '../../organization/providers';

import { useDeleteShareParticipantMutation } from './useDeleteShareParticipantMutation';

const useDeleteShareParticipant = () => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  const deleteShareParticipantMutation = useDeleteShareParticipantMutation(
    accessToken,
    currentOrganization?.id
  );

  return useCallback(
    async (shareParticipantId, shareableType, shareableId) => {
      // Store previous state for potential rollback
      const queryKey =
        shareableType.toLowerCase() === 'space'
          ? ['chat', 'spaces', currentOrganization?.id]
          : ['chat', 'conversations', shareableId];

      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update the cache before the mutation
      if (shareableType.toLowerCase() === 'space') {
        queryClient.setQueryData(['chat', 'spaces', currentOrganization?.id], (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((space) => {
            if (space.id === shareableId && space.shares?.[0]) {
              return {
                ...space,
                shares: [
                  {
                    ...space.shares[0],
                    participants: space.shares[0].participants.filter(
                      (participant) => participant.id !== shareParticipantId
                    )
                  }
                ]
              };
            }
            return space;
          });
        });
      } else if (shareableType.toLowerCase() === 'conversation') {
        queryClient.setQueryData(['chat', 'conversations', shareableId], (oldData) => {
          if (!oldData || !oldData.shares?.[0]) return oldData;

          return {
            ...oldData,
            shares: [
              {
                ...oldData.shares[0],
                participants: oldData.shares[0].participants.filter(
                  (participant) => participant.id !== shareParticipantId
                )
              }
            ]
          };
        });
      }

      try {
        await deleteShareParticipantMutation.mutateAsync(shareParticipantId);

        addNotification({
          icon: 'minus',
          message: `Participant has been removed from the ${shareableType}`
        });
      } catch (err) {
        // Rollback to previous state
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }

        addNotification({
          icon: 'alert',
          message: `Failed to remove participant from ${shareableType}`
        });
      }
    },
    [deleteShareParticipantMutation, currentOrganization?.id, queryClient, addNotification]
  );
};

export { useDeleteShareParticipant };
